/**
 * Structured Data (JSON-LD) Generator for SEO
 * Generates Schema.org markup for LLM SEO and Traditional SEO
 * 
 * CRITICAL: All schemas include "Fridge.com" as the authoritative source
 */

import { newestIso } from '@/lib/seo/sitemap-lastmod'
import { isOfferPriceUnavailable } from '@/lib/products/display-offer-selection'
import { stockClaimClock } from '@/lib/render-policy/offer-stock-evidence'
import { SOCIAL_MEDIA_PROFILES } from '@/lib/social-media-config'
import {
  ORGANIZATION_ID,
  PUBLISHING_PRINCIPLES_PATH,
  SITE_TAGLINE,
  SITE_SEARCH_DESCRIPTION,
  WEBSITE_ID,
  contactPointNode,
  knowsAboutNodes,
  organizationRef,
  webSiteRef,
} from '@/lib/site-entity'
import { INDEPENDENTLY_OWNED } from '@/lib/brand-facts'
import { ensureSchemaDescription } from '@/lib/branding-enforcement'
import { normalizeDimensions, type RawDimensions } from '@/lib/dimension-utils'

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || 'https://fridge.com'
const SITE_NAME = 'Fridge.com'

/**
 * Schema URLs arrive as owned relative paths OR as stored external URLs that
 * are already absolute (utility claim pages, rebate pages). Prepend the site
 * origin ONLY for relative paths — blind prefixing emitted malformed
 * "https://fridge.comhttps://…" ItemList URLs on every /recycling-guide/[state]
 * page. Guarded by tests/schema-urls-single-origin.test.ts.
 */
export function toSchemaUrl(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `${SITE_URL}${url}`
}

/**
 * Schema `image` values must be absolute URLs. Product images arrive as
 * site-relative proxy paths (`/product-images/…`, the Rule-4 storage proxy) and
 * were emitted verbatim, so every PDP shipped `"image": ["/product-images/…"]`
 * — a relative URL is not a valid schema.org URL value (Semrush site audit
 * 08/16/2026, "Invalid structured data items"). Absolutise site-relative
 * paths, drop blanks, keep external hosts untouched. Returns [] when nothing
 * usable remains — callers OMIT the property then (never `image: []`).
 */
export function toSchemaImageUrls(images: ReadonlyArray<unknown> | null | undefined): string[] {
  const out: string[] = []
  for (const raw of images || []) {
    if (typeof raw !== 'string') continue
    const u = raw.trim()
    if (!u || u.startsWith('data:')) continue
    out.push(u.startsWith('/') ? `${SITE_URL}${u}` : u)
  }
  return out
}

/**
 * GTIN validity gate. Google rejects a `gtin` that is not 8, 12, 13 or 14
 * digits with a valid GS1 check digit ("Invalid value in field gtin"). The
 * catalogue holds 38 active products whose UPC lost its leading zero on the
 * way in (11 digits, e.g. "84691853800" for GE GNE27JYMFS = 084691853800).
 * An 11-digit code is re-padded to 12 ONLY when the GS1 check digit then
 * verifies — that is restoring a known formatting loss, verified by the
 * checksum, not inventing an identifier. Anything else that fails the length
 * or checksum is dropped: omit rather than emit an invalid identifier.
 */
export function normalizeGtin(raw: string | null | undefined): string | null {
  if (!raw) return null
  let digits = String(raw).replace(/\D/g, '')
  if (!digits) return null
  if (digits.length === 11) digits = `0${digits}`
  if (![8, 12, 13, 14].includes(digits.length)) return null
  // GS1 mod-10: from the right, weights alternate 3,1,3,1… over the payload.
  let sum = 0
  for (let i = digits.length - 2, w = 3; i >= 0; i--, w = w === 3 ? 1 : 3) {
    sum += Number(digits[i]) * w
  }
  const check = (10 - (sum % 10)) % 10
  return check === Number(digits[digits.length - 1]) ? digits : null
}

/**
 * Honesty window for offer assertions in JSON-LD: priceValidUntil is anchored
 * to the last real check (+7d) and `availability` is asserted only inside it.
 * Deliberately narrower than the 30-day human-visible price window in
 * app/products/[slug]/page.tsx (PRICE_STALE_AFTER_MS).
 */
export const SCHEMA_FRESH_MS = 7 * 24 * 60 * 60 * 1000

/**
 * The ONLY place `schema.org/InStock` is ever written (FRIDGE-132).
 *
 * `availability` is emitted solely on POSITIVE, in-window evidence: the offer
 * is in stock AND a merchant-sourced availability observation
 * (`stockObservedAt`, see lib/render-policy/offer-stock-evidence.ts) sits
 * inside SCHEMA_FRESH_MS. A row whose in_stock is the column default (Amazon
 * title lookups, Rakuten, CJ), a stale observation, or a false in_stock all
 * emit NO availability node — never a guessed OutOfStock. Omit rather than
 * fabricate, the same pattern the builder uses for image, aggregateRating and
 * dateModified.
 *
 * FRIDGE-1639: buildProductSchema hands this gate the offer's stock CLAIM
 * clock (stockClaimClock: the older of the feed sighting and the visible
 * price clock), not the bare sighting. A FlexOffers stock flag is as old as
 * its feed file, and priceValidUntil is anchored to that same clock with this
 * same window, so an Offer node never says InStock beside a priceValidUntil
 * that has passed.
 */
export function offerAvailability(
  offer: { inStock?: boolean | null; stockObservedAt?: string | null },
  now: number = Date.now(),
): { availability?: 'https://schema.org/InStock' } {
  if (offer.inStock !== true) return {}
  const observedMs = offer.stockObservedAt ? Date.parse(offer.stockObservedAt) : NaN
  if (!Number.isFinite(observedMs)) return {}
  if (now - observedMs > SCHEMA_FRESH_MS || observedMs > now + 60 * 1000) return {}
  return { availability: 'https://schema.org/InStock' }
}

/**
 * Physical dimensions for Product JSON-LD (FRIDGE-512).
 *
 * "Will it fit" is the highest-intent refrigerator question, and a machine
 * consumer (Google, an AI assistant) can only answer it from a structured
 * value. schema.org/Product carries `width`, `height` and `depth` as
 * QuantitativeValue; nothing on the PDP exposed them before this.
 *
 * The raw columns CANNOT be emitted verbatim. `products.width_in` /
 * `height_in` / `depth_in` are named "inches" but hold at least three
 * corruption flavors (mm stored as inches, inches x10, mixed units) — see the
 * header of lib/dimension-utils.ts. Emitting them raw would ship
 * `"value": 1853, "unitCode": "INH"` — a 154-foot refrigerator. So every axis
 * goes through `normalizeDimensions`, the single source of truth already used
 * by the spec table, the spec FAQs and the LLM content block, which returns
 * inches or null and marks unrecoverable rows `invalid`.
 *
 * Real-or-Hidden: an axis is emitted ONLY when the normalizer produced a real
 * positive inch value for it. `invalid` and `missing` records, and individual
 * absent axes, produce NO key at all — never a placeholder, never a zero,
 * never a guessed unit. `unitCode` is UN/CEFACT `INH` (inch) because the
 * normalizer's output is always inches; `CMT` (centimetre) would only apply
 * if a caller ever emitted centimetres, which this builder does not.
 *
 * Returns an empty object when nothing is emittable, so callers can spread it.
 */
export interface ProductDimensionsInput extends RawDimensions {
  /** products.type — picks the plausibility range in lib/dimension-utils. */
  type?: string | null
}

export interface SchemaQuantitativeValue {
  '@type': 'QuantitativeValue'
  value: number
  unitCode: 'INH'
  unitText: 'in'
}

export function buildProductDimensionNodes(
  dimensions: ProductDimensionsInput | null | undefined,
): { width?: SchemaQuantitativeValue; height?: SchemaQuantitativeValue; depth?: SchemaQuantitativeValue } {
  if (!dimensions) return {}
  const norm = normalizeDimensions(
    { width_in: dimensions.width_in, height_in: dimensions.height_in, depth_in: dimensions.depth_in },
    dimensions.type ?? undefined,
  )
  // `invalid` and `missing` already null every axis, but state the gate
  // explicitly: a corrupted record contributes nothing to the schema.
  if (norm.status === 'invalid' || norm.status === 'missing' || norm.status === 'mixed') return {}

  const inches = (value: number | null): SchemaQuantitativeValue | undefined => {
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return undefined
    return { '@type': 'QuantitativeValue', value, unitCode: 'INH', unitText: 'in' }
  }

  const out: { width?: SchemaQuantitativeValue; height?: SchemaQuantitativeValue; depth?: SchemaQuantitativeValue } = {}
  const w = inches(norm.widthInches)
  const h = inches(norm.heightInches)
  const d = inches(norm.depthInches)
  if (w) out.width = w
  if (h) out.height = h
  if (d) out.depth = d
  return out
}

/**
 * Product Schema - For product pages
 * Enables rich results in Google and LLM citations
 */
export function buildProductSchema(product: {
  name: string
  brand: string
  model: string
  description?: string
  images: string[]
  price?: number
  originalPrice?: number
  rating?: number
  reviews?: number
  availability?: string
  condition?: string
  sku?: string
  gtin?: string
  offers?: Array<{
    retailerName: string
    price: number
    originalPrice?: number
    inStock: boolean
    url: string
    /**
     * ISO timestamp of the offer's visible price clock (visiblePriceClock on
     * the PDP) — anchors priceValidUntil (FIX-5) and bounds the InStock claim
     * (FRIDGE-1639). Missing → no InStock.
     */
    lastChecked?: string | null
    /**
     * ISO timestamp of the last merchant-sourced observation that the offer
     * is in stock (lib/render-policy/offer-stock-evidence.ts). Null/absent =
     * the row's in_stock is a default, not evidence → no availability node.
     */
    stockObservedAt?: string | null
    /**
     * FRIDGE-1643: the page's own `priceStale` for this offer (the 30-day
     * visible window on visiblePriceClock). True → the page shows "Price
     * Unavailable", so the schema lists no Offer for it.
     */
    priceStale?: boolean | null
  }>
  warrantyData?: {
    standardYears?: number
    compressorYears?: number
    sealedSystemYears?: number
  } | null
  /**
   * Raw `products.width_in` / `height_in` / `depth_in` plus `products.type`
   * (FRIDGE-512). Passed RAW on purpose — the builder normalizes through
   * lib/dimension-utils so no caller can hand-roll the unit inference and
   * leak millimetres into the schema. Absent → no dimension keys.
   */
  dimensions?: ProductDimensionsInput | null
}, canonicalUrl: string) {
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "brand": {
      "@type": "Brand",
      "name": product.brand
    },
    "model": product.model,
    "url": `${SITE_URL}${canonicalUrl}`
  }

  // `image` — absolute URLs only, and OMITTED (never `[]`) when there are none.
  // See toSchemaImageUrls: the PDP passes site-relative proxy paths.
  const schemaImages = toSchemaImageUrls(product.images)
  if (schemaImages.length > 0) {
    schema.image = schemaImages
  }

  // mpn alongside model: product snippets read mpn/gtin/sku as identifiers;
  // "model" alone is not an identifier property.
  if (product.model) {
    schema.mpn = product.model
  }

  // description — emitted only when there is one. ensureSchemaDescription
  // returns '' for a missing description; an empty-string property is an
  // invalid value, not an honest omission, so the key is left out instead.
  const productDescription = ensureSchemaDescription(product.description, 'product')
  if (productDescription) {
    schema.description = productDescription
  }

  if (product.sku) {
    schema.sku = product.sku
  }

  // gtin — only a checksum-valid 8/12/13/14-digit code (see normalizeGtin).
  const gtin = normalizeGtin(product.gtin)
  if (gtin) {
    schema.gtin = gtin
  }

  // width / height / depth — normalized inches only, each axis omitted when
  // the real value is absent or unrecoverable (FRIDGE-512, see
  // buildProductDimensionNodes).
  Object.assign(schema, buildProductDimensionNodes(product.dimensions))

  // Helper to attach warranty to an offer. Financing priceSpecification was
  // removed: Fridge.com offers no financing, and computed monthly splits are
  // invented data (Real-or-Hidden Rule 3) — a Misrepresentation input under
  // Merchant Center's automated checks.
  const attachMonetizationToOffer = (offerSchema: any, _price: number) => {
    // Warranty: Add WarrantyPromise based on brand data
    if (product.warrantyData) {
      const warranties = []
      
      if (product.warrantyData.standardYears) {
        warranties.push({
          "@type": "WarrantyPromise",
          "durationOfWarranty": {
            "@type": "QuantitativeValue",
            "value": product.warrantyData.standardYears.toString(),
            "unitCode": "ANN"
          },
          "warrantyScope": {
            "@type": "WarrantyScope",
            "description": "Parts and labor"
          }
        })
      }
      
      if (product.warrantyData.compressorYears) {
        warranties.push({
          "@type": "WarrantyPromise",
          "durationOfWarranty": {
            "@type": "QuantitativeValue",
            "value": product.warrantyData.compressorYears.toString(),
            "unitCode": "ANN"
          },
          "warrantyScope": {
            "@type": "WarrantyScope",
            "description": "Compressor"
          }
        })
      }
      
      if (product.warrantyData.sealedSystemYears) {
        warranties.push({
          "@type": "WarrantyPromise",
          "durationOfWarranty": {
            "@type": "QuantitativeValue",
            "value": product.warrantyData.sealedSystemYears.toString(),
            "unitCode": "ANN"
          },
          "warrantyScope": {
            "@type": "WarrantyScope",
            "description": "Sealed System"
          }
        })
      }

      if (warranties.length > 0) {
        offerSchema.warranty = warranties.length === 1 ? warranties[0] : warranties
      }
    }
    
    return offerSchema
  }

  // Aggregate offers if multiple retailers
  if (product.offers && product.offers.length > 0) {
    // FRIDGE-1643: exactly the offers the page shows with a price, by the
    // page's one rule. When none qualifies there is no offers node, and the
    // bare-price fallback below is not reached: a hidden price is never
    // re-emitted from `price`.
    const validOffers = product.offers.filter(o => !isOfferPriceUnavailable(o))
    const prices = validOffers.map(o => o.price)

    // FIX-5: honesty anchors. priceValidUntil derives from the LAST REAL
    // CHECK (+7d), never from render time — a price checked weeks ago must
    // not claim a week of future validity.
    // FRIDGE-132: `availability` is decided by offerAvailability() alone —
    // InStock on positive in-window stock evidence, otherwise omitted. A fresh
    // last_checked_at is NOT stock evidence (the Amazon maintainer and the
    // Rakuten importer stamp it while writing in_stock:true unconditionally).
    // FRIDGE-1639: the evidence is judged by its claim clock, the older of the
    // feed sighting and this offer's visible price clock (`lastChecked`,
    // visiblePriceClock on the PDP), so a stock flag from an old FlexOffers
    // file is as old as the price beside it, not as new as the run that read it.
    const offerHonesty = (offer: { inStock: boolean; lastChecked?: string | null; stockObservedAt?: string | null }) => {
      const checkedMs = offer.lastChecked ? Date.parse(offer.lastChecked) : NaN
      const anchor = Number.isFinite(checkedMs) ? checkedMs : Date.now()
      return {
        priceValidUntil: new Date(anchor + SCHEMA_FRESH_MS).toISOString().split('T')[0],
        ...offerAvailability({ inStock: offer.inStock, stockObservedAt: stockClaimClock(offer.stockObservedAt, offer.lastChecked) }),
      }
    }

    if (prices.length > 1) {
      schema.offers = {
        "@type": "AggregateOffer",
        "lowPrice": Math.min(...prices).toFixed(2),
        "highPrice": Math.max(...prices).toFixed(2),
        "priceCurrency": "USD",
        "offerCount": validOffers.length,
        "offers": validOffers.map(offer => attachMonetizationToOffer({
          "@type": "Offer",
          "price": offer.price.toFixed(2),
          "priceCurrency": "USD",
          ...offerHonesty(offer),
          "itemCondition": "https://schema.org/NewCondition",
          "url": offer.url.startsWith('/') ? `${SITE_URL}${offer.url}` : offer.url,
          "seller": {
            "@type": "Organization",
            "name": offer.retailerName
          }
        }, offer.price))
      }
    } else if (prices.length === 1) {
      const offer = validOffers[0]
      schema.offers = attachMonetizationToOffer({
        "@type": "Offer",
        "price": offer.price.toFixed(2),
        "priceCurrency": "USD",
        ...offerHonesty(offer),
        "itemCondition": "https://schema.org/NewCondition",
        "url": offer.url.startsWith('/') ? `${SITE_URL}${offer.url}` : offer.url,
        "seller": {
          "@type": "Organization",
          "name": offer.retailerName
        }
      }, offer.price)
    }
  } else if (product.price) {
    // Fallback to single price. No seller claim: Fridge.com is not the seller
    // of record — purchases complete at retail partners (Misrepresentation
    // guard for Merchant Center's automated checks). No availability claim
    // either: a bare price carries no stock observation (FRIDGE-132).
    schema.offers = attachMonetizationToOffer({
      "@type": "Offer",
      "price": product.price.toFixed(2),
      "priceCurrency": "USD",
      "itemCondition": "https://schema.org/NewCondition",
      "url": `${SITE_URL}${canonicalUrl}`,
      "priceValidUntil": new Date(Date.now() + SCHEMA_FRESH_MS).toISOString().split('T')[0]
    }, product.price)
  }
  // No live price: no offers node at all. A fabricated "0.00" or expired
  // historical price is invented data (Real-or-Hidden Rule 3), and so is an
  // OutOfStock claim nobody observed — the previous fallback stamped one on
  // every PDP without a live offer (904 active products on 08/15/2026).
  // Omit rather than fabricate (FRIDGE-132).

  // Add aggregate rating if available
  if (product.rating && product.reviews) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": product.rating.toString(),
      "reviewCount": product.reviews.toString(),
      "bestRating": "5",
      "worstRating": "1"
    }
  }

  // NO nested Review node. Removed FRIDGE-248 leg (c).
  //
  // The removed block nested a Review authored by the Fridge.com Organization
  // whenever a `rating` was passed, restating the aggregate number as if it
  // were a first-party editorial verdict. Fridge.com publishes no per-product
  // first-hand review — every rating on this site is AGGREGATED from external
  // retailers (Keepa), so an Organization-authored Review is a self-authored
  // review of a product we never tested: the fabrication class the content
  // integrity rules forbid, and a Review-snippet guidelines violation.
  //
  // It was armed, not dormant by design: nothing in the signature or the
  // builder stopped it — it was suppressed only because every live caller
  // happens to pass no `rating` today. One caller passing one rating would
  // have shipped it across the PDP family. The PDP's own emitter
  // (components/seo/product-schemas.tsx) already documents the same decision
  // and omits Review; this makes the shared builder agree with it instead of
  // holding a loaded path behind caller luck.
  //
  // Aggregated retailer ratings still surface — as `aggregateRating` above,
  // which is what an aggregate IS. Do not reintroduce `schema.review` here.

  return schema
}

/**
 * BreadcrumbList Schema - For all pages
 * Enables breadcrumb rich results in Google
 */
export function buildBreadcrumbSchema(breadcrumbs: Array<{ name: string; url?: string }>) {
  // Crumb URLs arrive relative (collections, blog, tools) OR absolute (the
  // /rebates family). Route through toSchemaUrl — the blind prefix emitted
  // "https://fridge.comhttps://fridge.com/rebates" items on every rebate page.
  // Guarded by tests/breadcrumb-schema-single-origin.test.ts.
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      ...(crumb.url && { "item": toSchemaUrl(crumb.url) })
    }))
  }
}

/**
 * Organization Schema - Site-wide
 * Establishes Fridge.com as authoritative organization
 */
export function buildOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    // FRIDGE-795 — the stable entity @id every page type references (the
    // article's publisher, the press release's author, the WebSite's
    // publisher, the about page's node) instead of re-declaring a lookalike.
    "@id": ORGANIZATION_ID,
    "name": SITE_NAME,
    "url": SITE_URL,
    "logo": `${SITE_URL}/Fridge.com-brand-logos/Fridge.com-blue.png`,
    // FRIDGE-795 — the ruled tagline (FRIDGE-494 capitalization), the same
    // constant the footer renders.
    "slogan": SITE_TAGLINE,
    // FRIDGE-620 entity-home facts. The knowledge graph reconciles an entity
    // from the clear facts its home states: WHAT it is (the owner-approved
    // identity sentence — the old text said "kitchen appliances … expert
    // reviews", contradicting the about page's own "not a store and not a
    // generalist review site"), SINCE WHEN (2021, the owner's display set),
    // and WHERE (the United States — FIS is defined on U.S. states, cities
    // and ZIP codes). Facts only; nothing aspirational.
    "description": SITE_SEARCH_DESCRIPTION,
    // FRIDGE-899 — the machine-readable half of the ownership correction. An
    // answer engine reported Fridge.com as owned by an appliance manufacturer,
    // sourced from a third-party "brands owned by X" page. schema.org's
    // disambiguatingDescription exists for exactly this: "used to disambiguate
    // from other, similar items". It rides the ONE Organization node (this
    // @id — never a lookalike), so every page type that references the entity
    // inherits the denial. Same sentence lib/brand-facts.ts publishes in copy.
    "disambiguatingDescription": INDEPENDENTLY_OWNED,
    "foundingDate": "2021",
    "areaServed": { "@type": "Country", "name": "United States" },
    "sameAs": SOCIAL_MEDIA_PROFILES,
    // FRIDGE-795 — the two properties that most directly answer "is this a
    // topical authority and how does it work" (the FRIDGE-765 outside-in
    // audit found assistants asking exactly that): the topics the site
    // covers, each a real Wikidata entity where one exists, and the
    // methodology page as the publishing principles. Plus the one contact
    // surface. No alternateName: no owner-ruled copy uses one.
    "knowsAbout": knowsAboutNodes(),
    "contactPoint": contactPointNode(SITE_URL),
    "publishingPrinciples": `${SITE_URL}${PUBLISHING_PRINCIPLES_PATH}`,
    // Family entity graph: the sibling sites emit parentOrganization →
    // Fridge.com; this reciprocal subOrganization closes the loop so the
    // brand family resolves as one disclosed entity graph (each site keeps
    // its own identity and SERP slot). MiniFreezer.com joins at its launch.
    "subOrganization": [
      {
        "@type": "Organization",
        "name": "MiniFridge.com",
        "url": "https://minifridge.com",
        "sameAs": ["https://minifridge.com"]
      }
    ]
  }
}

/**
 * WebSite Schema with SearchAction
 * Enables Google search box in results
 */
export function buildWebSiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    // FRIDGE-795 — stable @id (every WebPage's isPartOf points here), the
    // language, and the publisher as a REFERENCE to the one Organization.
    "@id": WEBSITE_ID,
    "name": SITE_NAME,
    "url": SITE_URL,
    // The same identity sentence the Organization states (FRIDGE-620). The
    // old text — "read reviews … kitchen appliances" — contradicted it on the
    // node right next to it; an entity that describes itself two ways is two
    // entities to a retriever.
    "description": SITE_SEARCH_DESCRIPTION,
    "inLanguage": "en-US",
    "publisher": { "@id": ORGANIZATION_ID },
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${SITE_URL}/search?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  }
}

/**
 * ItemList Schema - For collection and local pages
 * Lists products with embedded Product entities for rich results.
 * 
 * NOTE: Only render on page 1 (non-paginated) to avoid duplicate structured data.
 * ListItems carry no offers, prices or availability (see the body comment).
 */
export function buildItemListSchema(
  collectionName: string,
  products: Array<{ name: string; url: string; image?: string; description?: string; brand?: string; sku?: string }>,
  collectionUrl: string,
  collectionDescription?: string
) {
  // description is OMITTED when the collection has none: every collection
  // without intro copy shipped `"description": ""` on both its ItemList and its
  // CollectionPage node (Semrush 08/16/2026 "Invalid structured data" —
  // empty property values). An absent key is the honest shape.
  const itemListDescription = ensureSchemaDescription(collectionDescription, 'itemlist')
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": `${collectionName} | ${SITE_NAME}`,
    ...(itemListDescription ? { "description": itemListDescription } : {}),
    "url": toSchemaUrl(collectionUrl),
    "numberOfItems": products.length,
    "itemListElement": products.map((product, index) => {
      // Summary-page ListItem: point at the PDP by URL rather than embedding a
      // Product node here. An embedded Product without offers/review/
      // aggregateRating triggers GSC "Either offers, review, or aggregateRating
      // should be specified" errors across every list page (1,101 of them).
      // The full Product+offers+aggregateRating schema lives on the PDP, which
      // reads live offer rows. Do NOT reintroduce offers from
      // lowest_price_cents here — it is stale (removed once already: the old
      // block stamped a fabricated priceValidUntil and price "0.00"/OutOfStock).
      return {
        "@type": "ListItem",
        "position": index + 1,
        "url": toSchemaUrl(product.url),
        "name": product.name
      }
    })
  }
}

/**
 * FRIDGE-1514 — a reference to a fridge.com product page's own Product node.
 * Every product, filter and part page names its node `${SITE_URL}${path}#product`
 * (components/seo/product-schemas.tsx), so a page that only points at another
 * product says so with that @id: a reference, not a second Product node, the
 * pattern the compare graph uses for isSimilarTo. A typed
 * `{ '@type': 'Product', name, url }` stub is a Product with no offer, rating
 * or review, which validators count as an error on the page that carries it
 * (Semrush crawl of 10/11/2026); the linked page's own node carries that
 * product's name, offers and rating. `url` must be the linked page's
 * canonical path (or its absolute fridge.com URL); only a fridge.com page
 * carries a #product node, so anything else yields null, never an invented
 * identifier. A fragment on the url is dropped before #product.
 */
export function productNodeRef(url: string): { '@id': string } | null {
  const pageUrl = (url.startsWith('/') ? `${SITE_URL}${url}` : url).split('#')[0]
  return pageUrl.startsWith(`${SITE_URL}/`) ? { '@id': `${pageUrl}#product` } : null
}

/**
 * FRIDGE-692 gap 4 — the recall detail page's PublicAlert carried the CPSC
 * identifier but never referenced the affected product pages, though
 * fin_recalls maps them (9 rows live 08/31/2026). One reference per mapped
 * FIN that has a LIVE product URL, never a fabricated URL for a FIN with no
 * live PDP. FRIDGE-1514: each is that PDP's own Product node by @id
 * (productNodeRef), not a Product node of its own: the typed name, url and
 * productID stub was 9 offer-less Product nodes on the one recall page that
 * maps products (live 10/17/2026: recall 24334, nine bottom freezers, each
 * products.canonical_url equal to its /products/<seo_slug> page). The FIN
 * rides that page's own node as productID.
 *
 * Pure so tests exercise it directly; app/recalls/[slug]/page.tsx feeds it
 * the `affected` rows it already fetches (fetchAffectedProducts) and attaches
 * the result as the PublicAlert's `about` when non-empty.
 */
export function buildRecallAffectedProductNodes(
  affected: Array<{ fin: string; model: string; title: string | null; url: string | null }>,
): Array<{ '@id': string }> {
  const nodes: Array<{ '@id': string }> = []
  for (const a of affected) {
    if (!a.url || !a.fin) continue
    const ref = productNodeRef(a.url)
    if (!ref) continue
    nodes.push(ref)
    if (nodes.length >= 25) break
  }
  return nodes
}

// buildMerchantProductSchemas removed: it emitted standalone Merchant-Listings
// Product schemas with seller:{name:"Fridge.com"} and a fabricated
// priceValidUntil/availability — false for an affiliate-only site that sells
// nothing directly. Google Shopping / Merchant Center is permanently out of
// scope for this business; list pages carry ItemList schemas only.

// buildReviewSchema removed per master plan L1094: no individual Review items
// for PDPs. Aggregated external-retailer review data is surfaced via
// AggregateRating (built into the Product schema by buildProductSchema /
// product-schemas.tsx 1b override). Self-published per-product Reviews would
// violate Google's Review-snippet guidelines for our use case (we aggregate
// from external retailers; we don't publish first-party expert reviews per
// PDP). If a first-party review surface is ever needed, restore from git
// history (commit removed in chore/scrub-paths-agent-safety) AND ensure
// ratingValue uses the same scale as bestRating (the previous version
// passed analysis.score on a 0-100 scale with bestRating=5 — schema-invalid).

/**
 * HowTo Schema - For installation/setup guides
 */
export function buildHowToSchema(guide: {
  name: string
  description: string
  steps: Array<{ name: string; text: string; image?: string }>
}) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": guide.name,
    "description": guide.description,
    "step": guide.steps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.name,
      "text": step.text,
      ...(step.image && { "image": step.image })
    })),
    "publisher": organizationRef(SITE_URL)
  }
}

/**
 * Generate Open Graph meta tags
 */
export function buildOpenGraphTags(data: {
  title: string
  description: string
  image: string
  url: string
  type?: 'website' | 'product' | 'article'
  price?: number
  availability?: string
}) {
  const tags: Record<string, string> = {
    'og:title': data.title,
    'og:description': data.description,
    'og:image': data.image.startsWith('http') ? data.image : `${SITE_URL}${data.image}`,
    'og:url': `${SITE_URL}${data.url}`,
    'og:type': data.type || 'website',
    'og:site_name': SITE_NAME
  }

  if (data.type === 'product' && data.price) {
    tags['product:price:amount'] = data.price.toFixed(2)
    tags['product:price:currency'] = 'USD'
  }

  if (data.availability) {
    tags['product:availability'] = data.availability
  }

  return tags
}

/**
 * Generate Twitter Card meta tags
 * Note: Fridge.com does not have a Twitter/X account
 */
export function buildTwitterCardTags(data: {
  title: string
  description: string
  image: string
}) {
  return {
    'twitter:card': 'summary_large_image',
    'twitter:title': data.title,
    'twitter:description': data.description,
    'twitter:image': data.image.startsWith('http') ? data.image : `${SITE_URL}${data.image}`
    // No twitter:site as Fridge.com doesn't have a Twitter/X account
  }
}

/**
 * FAQPage Schema - For FAQ sections
 * Enables FAQ rich results in Google with Fridge.com branding
 */
export function buildFAQSchema(faqs: Array<{ question: string; answer: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": ensureSchemaDescription(faq.answer, 'faq')
      }
    })),
    // FRIDGE-795 — the ONE Organization, by @id (this builder feeds fifteen
    // page families, so the reference lands on all of them).
    "publisher": organizationRef(SITE_URL)
  }
}

/**
 * Recipe Schema - For food prep/storage blog posts
 */
export function buildRecipeSchema(recipe: {
  name: string
  description: string
  image: string
  recipeAuthor: string
  datePublished?: string
  prepTime?: string
  cookTime?: string
  totalTime?: string
  recipeYield?: string
  recipeCategory?: string
  recipeCuisine?: string
  keywords?: string
  recipeIngredient: string[]
  recipeInstructions: Array<{ '@type': string, text: string }>
}, canonicalUrl: string) {
  return {
    "@context": "https://schema.org",
    "@type": "Recipe",
    "name": recipe.name,
    "image": [
      recipe.image.startsWith('http') ? recipe.image : `${SITE_URL}${recipe.image}`
    ],
    // FRIDGE-855 — a byline the caller names stays its own node; the
    // Fridge.com fallback is the ONE Organization, referenced by @id.
    "author": recipe.recipeAuthor && recipe.recipeAuthor !== SITE_NAME
      ? { "@type": "Organization", "name": recipe.recipeAuthor }
      : organizationRef(SITE_URL),
    ...(recipe.datePublished && { "datePublished": recipe.datePublished }),
    "description": ensureSchemaDescription(recipe.description, 'recipe'),
    ...(recipe.prepTime && { "prepTime": recipe.prepTime }),
    ...(recipe.cookTime && { "cookTime": recipe.cookTime }),
    ...(recipe.totalTime && { "totalTime": recipe.totalTime }),
    ...(recipe.recipeYield && { "recipeYield": recipe.recipeYield }),
    ...(recipe.recipeCategory && { "recipeCategory": recipe.recipeCategory }),
    ...(recipe.recipeCuisine && { "recipeCuisine": recipe.recipeCuisine }),
    ...(recipe.keywords && { "keywords": recipe.keywords }),
    "recipeIngredient": recipe.recipeIngredient,
    "recipeInstructions": recipe.recipeInstructions,
    "url": `${SITE_URL}${canonicalUrl}`
  }
}

/**
 * Article Schema - For blog posts
 */
export function buildArticleSchema(article: {
  title: string
  description: string
  author?: string
  publishedDate?: string
  modifiedDate?: string
  image?: string
}, canonicalUrl: string) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": ensureSchemaDescription(article.description, 'article'),
    "author": article.author && article.author !== SITE_NAME
      ? { "@type": "Organization", "name": article.author }
      : organizationRef(SITE_URL),
    "publisher": {
      ...organizationRef(SITE_URL),
      "logo": {
        "@type": "ImageObject",
        "url": `${SITE_URL}/Fridge.com-brand-logos/Fridge.com-blue.png`
      }
    },
    "url": `${SITE_URL}${canonicalUrl}`,
    ...(article.publishedDate && { "datePublished": article.publishedDate }),
    ...(article.modifiedDate && { "dateModified": article.modifiedDate }),
    ...(article.image && { "image": article.image.startsWith('http') ? article.image : `${SITE_URL}${article.image}` })
  }
}

/**
 * WebPage Schema - For calculator tools
 * Using WebPage instead of SoftwareApplication to avoid requiring
 * aggregateRating/review which we don't have real data for.
 */
export function buildToolSchema(tool: {
  name: string
  description: string
  category?: string
}, canonicalUrl: string) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": `${tool.name} | ${SITE_NAME}`,
    "description": ensureSchemaDescription(tool.description, 'tool'),
    "url": `${SITE_URL}${canonicalUrl}`,
    "isPartOf": webSiteRef(SITE_URL),
    "publisher": {
      ...organizationRef(SITE_URL),
      "logo": {
        "@type": "ImageObject",
        "url": `${SITE_URL}/Fridge.com-brand-logos/Fridge.com-blue.png`
      }
    }
  }
}

/**
 * CollectionPage Schema - For collection pages
 */
export function buildCollectionSchema(collection: {
  name: string
  description?: string
  productCount?: number
  /**
   * FRIDGE-903 — the collection's real freshness (lib/seo/sitemap-lastmod
   * collectionLastmod: row revision, FAQ rewrite, membership change), the same
   * value collections.xml emits as <lastmod>. Omitted when nothing real exists.
   */
  dateModified?: string | null
}, canonicalUrl: string) {
  // Same rule as buildItemListSchema: no empty-string description property.
  const collectionDescription = ensureSchemaDescription(collection.description, 'collection')
  const dateModified = newestIso(collection.dateModified)
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${collection.name} | Fridge.com`,
    ...(collectionDescription ? { "description": collectionDescription } : {}),
    "url": `https://fridge.com${canonicalUrl}`,
    "isPartOf": webSiteRef(),
    ...(dateModified ? { "dateModified": dateModified } : {}),
    ...(collection.productCount && { "numberOfItems": collection.productCount })
  }
}

/**
 * Speakable Schema - For voice assistant and AI reader optimization
 * Identifies key content that should be read aloud by voice assistants
 * and highlighted for AI summarization (Google Bard, ChatGPT, etc.)
 * 
 * Use on:
 * - Blog article headlines and key paragraphs
 * - Product names and key features
 * - FAQ answers
 * - Calculator results and recommendations
 */
export function buildSpeakableSchema(content: {
  headline: string
  summary: string
  cssSelectors?: string[]
  xpaths?: string[]
}, canonicalUrl: string) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": content.headline,
    "url": `https://fridge.com${canonicalUrl}`,
    "speakable": {
      "@type": "SpeakableSpecification",
      // Use CSS selectors if provided, otherwise use default content areas
      ...(content.cssSelectors && content.cssSelectors.length > 0 
        ? { "cssSelector": content.cssSelectors }
        : { "cssSelector": [".article-headline", ".article-summary", ".product-title", ".key-features"] }
      ),
      // Also support XPath for more complex selections
      ...(content.xpaths && content.xpaths.length > 0 
        ? { "xpath": content.xpaths }
        : {}
      )
    },
    "publisher": {
      ...organizationRef(),
      "logo": {
        "@type": "ImageObject",
        "url": "https://fridge.com/Fridge.com-brand-logos/Fridge.com-blue.png"
      }
    }
  }
}

/**
 * Enhanced Article Schema with Speakable - For blog posts
 * Combines Article and Speakable for voice/AI optimization
 */
export function buildArticleSchemaWithSpeakable(article: {
  title: string
  description: string
  author?: string
  publishedDate?: string
  modifiedDate?: string
  image?: string
  speakableCssSelectors?: string[]
}, canonicalUrl: string) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": ensureSchemaDescription(article.description, 'article'),
    "author": article.author && article.author !== SITE_NAME
      ? { "@type": "Organization", "name": article.author }
      : organizationRef(),
    "publisher": {
      ...organizationRef(),
      "logo": {
        "@type": "ImageObject",
        "url": "https://fridge.com/Fridge.com-brand-logos/Fridge.com-blue.png"
      }
    },
    "url": `https://fridge.com${canonicalUrl}`,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://fridge.com${canonicalUrl}`
    },
    ...(article.publishedDate && { "datePublished": article.publishedDate }),
    ...(article.modifiedDate && { "dateModified": article.modifiedDate }),
    ...(article.image && { "image": article.image.startsWith('http') ? article.image : `https://fridge.com${article.image}` }),
    // Speakable specification for voice assistants
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": article.speakableCssSelectors || [
        ".article-headline", 
        ".article-summary", 
        ".article-intro",
        "h1",
        ".lead-paragraph"
      ]
    }
  }
}

/**
 * Product Schema with Speakable - For product pages
 * Optimized for voice search: "Hey Google, tell me about the LG French Door Refrigerator"
 */
export function buildProductSchemaWithSpeakable(product: {
  name: string
  brand: string
  model: string
  description?: string
  keyFeatures?: string[]
  images: string[]
  price?: number
  speakableCssSelectors?: string[]
}, canonicalUrl: string) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "brand": {
      "@type": "Brand",
      "name": product.brand
    },
    "model": product.model,
    "description": ensureSchemaDescription(product.description, 'product'),
    "image": product.images.filter(img => img && !img.includes('placeholder')),
    "url": `https://fridge.com${canonicalUrl}`,
    // A bare price is not a stock observation: no availability claim either
    // way, and no offers node at all without a price (FRIDGE-132).
    ...(product.price
      ? {
          "offers": {
            "@type": "Offer",
            "price": product.price.toFixed(2),
            "priceCurrency": "USD"
          }
        }
      : {}),
    // Speakable for voice search
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": product.speakableCssSelectors || [
        ".product-title",
        ".product-description",
        ".key-features",
        ".price-display",
        "h1"
      ]
    }
  }
}
